"use client";

import React from "react";
import { motion } from "framer-motion";

import Image from "next/image";
import { PortableText } from "next-sanity";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { PageKryptoDaySchema } from "@/sanity/pages/PageKryptoDayTemplate";

export default function KryptoDaySpeakers({
  data,
}: {
  data: PageKryptoDaySchema;
}) {
  return (
    <section className="flex relative flex-col gap-[2rem] z-[4] overflow-clip">
      {/* Title */}
      <div className="mx-auto w-fit">
        {data?.speakersTitle && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="w-[min(100rem,100%)] mb-[4rem] richtext richtext-center prose-p:text-white/70 prose-p:text-[2rem] prose-p:w-[min(47.375rem,100%)]  prose-h2:font-[600] prose-h2:bg-gradient-to-b prose-h2:from-[#FFFFFF] prose-h2:to-[#999999] prose-h2:bg-clip-text prose-h2:text-transparent"
          >
            <PortableText
              value={data?.speakersTitle}
              components={customRichTextComponent({})}
            />
          </motion.div>
        )}
      </div>

      {/* Speaker cards */}
      <div className="md:h-[67rem] w-[min(148.5rem,100%)] flex md:mx-auto gap-[2rem] lg:gap-[4rem] max-md:overflow-x-auto max-md:snap-x max-md:snap-mandatory max-md:scroll-smooth max-md:px-[2rem] scrollbar-hide">
        {data?.speakers &&
          data?.speakers.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{
                duration: 0.7,
                delay: index * 0.15,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="relative md:w-full h-[67rem] max-md:min-w-[85vw] max-md:snap-center max-md:flex-shrink-0"
            >
              <div className="absolute h-full">
                <Image src={item.image} alt="" className="h-full" />
              </div>
              <div className="flex absolute flex-col z-[4] bottom-[1.25rem] left-[1.25rem] gap-[1rem]">
                {/* Logo badge */}
                <div
                  className={`relative border rounded-[1rem] py-[1.75rem] px-[4rem] bg-white/[3%] overflow-hidden ${
                    index === 0
                      ? "border-[#3EB791]/80"
                      : index === 1
                        ? "border-[#7188FF]/80"
                        : "border-[#697CF4]/80"
                  }`}
                >
                  <div
                    className={`absolute top-0 left-0 bg-opacity-60 -translate-x-1/2 -translate-y-1/2 rotate-[25deg] w-[10rem] h-[6rem] ${
                      index === 0
                        ? "bg-[#61FFCE]"
                        : index === 1
                          ? "bg-[#7188FF]"
                          : "bg-[#697CF4]"
                    }`}
                  />

                  <div
                    className={`absolute right-0 bottom-0 bg-opacity-60 translate-x-1/2 translate-y-1/2 rotate-[25deg] w-[10rem] h-[6rem] ${
                      index === 0
                        ? "bg-[#61FFCE]"
                        : index === 1
                          ? "bg-[#7188FF]"
                          : "bg-[#697CF4]"
                    }`}
                  />

                  <div className="absolute inset-0 backdrop-blur-[4rem] bg-white/[1%]" />

                  <Image
                    src={item.logo}
                    alt=""
                    className="relative h-full z-[1]"
                  />
                </div>

                {/* Name badge */}
                <div
                  className={`relative border rounded-[1rem] py-[1.75rem] px-[2rem] bg-white/[3%] overflow-hidden ${
                    index === 0
                      ? "border-[#3EB791]/80"
                      : index === 1
                        ? "border-[#7188FF]/80"
                        : "border-[#697CF4]/80"
                  }`}
                >
                  <div
                    className={`absolute top-0 left-0 bg-opacity-60 -translate-x-1/2 -translate-y-1/2 rotate-[25deg] w-[10rem] h-[6rem] ${
                      index === 0
                        ? "bg-[#61FFCE]"
                        : index === 1
                          ? "bg-[#7188FF]"
                          : "bg-[#697CF4]"
                    }`}
                  />

                  <div
                    className={`absolute right-0 bottom-0 bg-opacity-60 translate-x-1/2 translate-y-1/2 rotate-[25deg] w-[10rem] h-[6rem] ${
                      index === 0
                        ? "bg-[#61FFCE]"
                        : index === 1
                          ? "bg-[#7188FF]"
                          : "bg-[#697CF4]"
                    }`}
                  />

                  <div className="absolute inset-0 backdrop-blur-[4rem] bg-white/[1%]" />

                  <div className="flex relative flex-col z-[1] gap-[.5rem]">
                    <p className="text-white m-0 text-[2rem] font-[500] leading-[120%]">
                      {item.name}
                    </p>
                    <p className="text-white/80 m-0 text-[2rem] leading-[120%]">
                      {item.position}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
      </div>
    </section>
  );
}
